import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUserProfile } from "@/hooks/useUserProfile";

export interface AutomationScore {
  score: number;
  tier: string;
  breakdown: Record<string, { score: number; notes: string }>;
  reasoning: string;
}

export interface SOP {
  id: string;
  title: string;
  purpose: string | null;
  trigger_event: string | null;
  prerequisites: string | null;
  tools: string | null;
  steps: any;
  decision_rules: string | null;
  exceptions: string | null;
  handoffs: string | null;
  qa_checklist: any;
  metrics: string | null;
  missing_to_confirm: any;
  owner_role: string | null;
  status: string | null;
  version: number | null;
  created_at: string;
  updated_at: string;
  automation_score?: AutomationScore | null;
}

interface UseSOPsOptions {
  organizationId?: string;
  enabled?: boolean;
}

async function fetchSOPs(organizationId: string): Promise<SOP[]> {
  // Fetch SOPs
  const { data: sopsData, error: sopsError } = await supabase
    .from("sops")
    .select("*")
    .eq("organization_id", organizationId)
    .order("created_at", { ascending: false });

  if (sopsError) {
    throw new Error(`Error fetching SOPs: ${sopsError.message}`);
  }

  // Fetch automation scores
  const { data: scoresData } = await supabase
    .from("sop_automation_scores")
    .select("*")
    .eq("organization_id", organizationId);

  // Merge scores into SOPs
  return (sopsData || []).map((sop) => {
    const scoreRecord = (scoresData || []).find((s) => s.sop_id === sop.id);
    return {
      ...sop,
      automation_score: scoreRecord
        ? {
            score: scoreRecord.score,
            tier: scoreRecord.tier,
            breakdown: scoreRecord.breakdown,
            reasoning: scoreRecord.reasoning,
          }
        : null,
    };
  });
}

/**
 * Shared hook for fetching SOPs with TanStack Query caching.
 * Data is cached for 5 minutes and shared across all components.
 *
 * Usage:
 * - In components: const { data: sops, isLoading, refetch } = useSOPs();
 * - When creating/updating/deleting SOPs: use the mutation hooks or call invalidateSOPs()
 */
export function useSOPs(options: UseSOPsOptions = {}) {
  const { organization } = useUserProfile();
  const orgId = options.organizationId || organization?.id;

  return useQuery({
    queryKey: ["sops", orgId],
    queryFn: () => fetchSOPs(orgId!),
    enabled: (options.enabled ?? true) && !!orgId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Fetch a single SOP by ID.
 */
export function useSOP(sopId: string | undefined) {
  const { organization } = useUserProfile();

  return useQuery({
    queryKey: ["sop", sopId],
    queryFn: async () => {
      if (!sopId) throw new Error("SOP ID required");

      const { data, error } = await supabase
        .from("sops")
        .select("*")
        .eq("id", sopId)
        .single();

      if (error) throw error;
      return data as SOP;
    },
    enabled: !!sopId && !!organization?.id,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to get the invalidation function for SOPs cache.
 * Call this after creating, updating, or deleting SOPs.
 */
export function useInvalidateSOPs() {
  const queryClient = useQueryClient();

  return {
    /**
     * Invalidate all SOP-related caches
     */
    invalidateAll: () => {
      queryClient.invalidateQueries({ queryKey: ["sops"] });
      queryClient.invalidateQueries({ queryKey: ["sop"] });
      // Also invalidate dashboard which depends on SOP data
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
    /**
     * Invalidate a specific SOP
     */
    invalidateSOP: (sopId: string) => {
      queryClient.invalidateQueries({ queryKey: ["sop", sopId] });
      queryClient.invalidateQueries({ queryKey: ["sops"] });
    },
    /**
     * Invalidate SOPs list only
     */
    invalidateList: () => {
      queryClient.invalidateQueries({ queryKey: ["sops"] });
    },
  };
}

/**
 * Hook for deleting SOPs with automatic cache invalidation.
 */
export function useDeleteSOP() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sopId: string) => {
      const { error } = await supabase.from("sops").delete().eq("id", sopId);

      if (error) throw error;
      return sopId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sops"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

/**
 * Hook for bulk deleting SOPs with automatic cache invalidation.
 */
export function useBulkDeleteSOPs() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sopIds: string[]) => {
      const { error } = await supabase.from("sops").delete().in("id", sopIds);

      if (error) throw error;
      return sopIds;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sops"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

/**
 * Hook for updating SOP status (e.g., approving) with automatic cache invalidation.
 */
export function useUpdateSOPStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sopId,
      status,
    }: {
      sopId: string;
      status: string;
    }) => {
      const { error } = await supabase
        .from("sops")
        .update({ status, updated_at: new Date().toISOString() })
        .eq("id", sopId);

      if (error) throw error;
      return { sopId, status };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["sop", variables.sopId] });
      queryClient.invalidateQueries({ queryKey: ["sops"] });
    },
  });
}

/**
 * Utility to invalidate SOPs cache from outside React components.
 * Use sparingly - prefer useInvalidateSOPs hook when possible.
 */
export function invalidateSOPsCache(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  queryClient.invalidateQueries({ queryKey: ["sops"] });
  queryClient.invalidateQueries({ queryKey: ["sop"] });
  queryClient.invalidateQueries({ queryKey: ["dashboard"] });
}
